import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { blockActions, twoFAActions } from '@/redux/actions';
import { twoFASelectors } from '@/redux/selectors';
import { externalLinks } from '@/config';
import { useInputText } from '@/hooks';
import { Validation } from '@/models';
import { UpdateTwoFAUsageRequest } from '@/api';

import { AlertBox, Button, InputGroup, InputMask } from '@/components';
import { ModalInfo, ModalInfoProps } from '../ModalInfo';

import styles from './TurnOff2FA.module.sass';

type TurnOff2FAProps = ModalInfoProps;

const TurnOff2FA: FC<TurnOff2FAProps> = (props) => {
  const { isOpen, onClose } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { loading, error } = useAppSelector(twoFASelectors.getTwoFASlice);

  const TOKEN_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'regex',
        value: /\d{6}/i,
        message: t('translation.invalid_code'),
      },
    ],
    [t],
  );

  const tokenField = useInputText('', TOKEN_VALIDATIONS);

  useEffect(() => {
    if (isOpen) {
      dispatch(blockActions.block());
    } else {
      dispatch(blockActions.unblock());
      tokenField.onReset();
    }
    dispatch(twoFAActions.setError(null));
  }, [isOpen]);

  const submitHandler = () => {
    if (!loading) {
      if (tokenField.isValid) {
        const payload: UpdateTwoFAUsageRequest = {
          token: tokenField.value,
          usage: false,
        };

        dispatch(twoFAActions.disableTwoFA(payload));
      } else if (!tokenField.isValid) {
        tokenField.onBlur();
      }
    }
  };

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.2fa_title')}
      className={styles.modal}
      onClose={onClose}
    >
      <div className={styles.form}>
        <div className={styles.description}>
          <span>{t('translation.2fa_clients_lnk_deactivate')}</span>
          <br />
          Google Authenticator (
          <a
            href={externalLinks.GOOGLE_AUTHENTICATOR_APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            App Store
          </a>
          &nbsp;
          {t('or')}&nbsp;
          <a
            href={externalLinks.GOOGLE_AUTHENTICATOR_GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Play
          </a>
          )&nbsp;{t('or')}&nbsp;
          <a
            href={externalLinks.AUTHY_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Authy
          </a>
        </div>

        <InputGroup
          label={t('translation.2fa_code_str')}
          className={styles.wrap}
        >
          <InputMask
            id="code"
            name="code"
            value={tokenField.value}
            onChange={tokenField.onChange}
            onBlur={tokenField.onBlur}
            className={styles.yourCode}
            format="### ###"
            mask="_"
            placeholder={t('translation.enter_2fa_code_str')!}
            disabled={loading}
            isError={tokenField.isDirty && !tokenField.isValid}
            required
          />
          {tokenField.isDirty && !tokenField.isValid && (
            <AlertBox type="danger" small>
              {{ description: tokenField.errors[0] }}
            </AlertBox>
          )}
        </InputGroup>

        {!!error && (
          <AlertBox type="danger" className={styles.alert}>
            {{
              title: parse(t('translation.ooops')),
              description: error,
              icon: '🤔',
            }}
          </AlertBox>
        )}

        <Button
          type="primary"
          corner="rect"
          minWidth={280}
          onClick={submitHandler}
          isLoading={loading}
          disabled={loading}
        >
          {t('translation.turn_off_btn')}
        </Button>
      </div>
    </ModalInfo>
  );
};

export default TurnOff2FA;
